import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, ChevronUp, Copy, X } from 'lucide-react';

const statusClass = (status) => {
  if (status === 'error') return 'text-red-700 bg-red-50 border-red-200';
  if (status === 'warning') return 'text-amber-800 bg-amber-50 border-amber-200';
  if (status === 'skipped') return 'text-gray-600 bg-gray-50 border-gray-200';
  return 'text-emerald-800 bg-emerald-50 border-emerald-200';
};

const StepBlock = ({ step }) => {
  const [open, setOpen] = useState(false);
  const status = step?.status || 'ok';

  return (
    <div className={`border rounded-md mb-2 overflow-hidden ${statusClass(status)}`}>
      <button
        type="button"
        className="w-full flex items-start gap-2 px-3 py-2 text-left text-xs font-medium"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 mt-0.5" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 mt-0.5" />
        )}
        <span className="min-w-0 flex-1">
          <span className="font-mono text-[10px] opacity-80">{step.id}</span>
          <span className="mx-1">·</span>
          {step.name}
          {step.durationMs != null ? (
            <span className="ml-2 font-normal opacity-70">({step.durationMs}ms)</span>
          ) : null}
        </span>
        <span className="uppercase text-[10px] tracking-wide shrink-0">{status}</span>
      </button>
      {open ? (
        <div className="px-3 pb-3 space-y-2 text-[11px] border-t border-current/10 bg-white/60">
          {step.error ? (
            <pre className="whitespace-pre-wrap break-words text-red-700">{step.error}</pre>
          ) : null}
          {step.input ? (
            <div>
              <div className="font-semibold text-gray-700 mb-1">Input</div>
              <pre className="whitespace-pre-wrap break-words max-h-40 overflow-auto bg-white/80 p-2 rounded border text-gray-800">
                {JSON.stringify(step.input, null, 2)}
              </pre>
            </div>
          ) : null}
          {step.output ? (
            <div>
              <div className="font-semibold text-gray-700 mb-1">Output</div>
              <pre className="whitespace-pre-wrap break-words max-h-64 overflow-auto bg-white/80 p-2 rounded border text-gray-800">
                {JSON.stringify(step.output, null, 2)}
              </pre>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const VRE_TECH_LABELS = {
  solar: 'Solar',
  windOn: 'Onshore wind',
  windOff: 'Offshore wind',
};

const FLEX_FUEL_LABELS = {
  bio: 'Bio',
  coal: 'Coal',
  gas: 'Gas',
  oil: 'Oil',
};

const FLEX_FUEL_TOTALS_KEYS = {
  bio: 'bioGw',
  coal: 'coalGw',
  gas: 'gasGw',
  oil: 'oilGw',
};

const formatUsdPerMwh = (value, { lastResort = false } = {}) => {
  if (lastResort || (typeof value === 'number' && Number.isFinite(value) && value >= 1e9)) {
    return 'last (no model gen)';
  }
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `$${value.toFixed(2)}/MWh`;
};

const formatTwh = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${value.toFixed(2)} TWh`;
};

const formatCf = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
};

const DispatchMeritSection = ({ report, totals, simulationDispatch, unmappedFlex = [] }) => {
  if (!report) return null;

  const flexStack = Array.isArray(report.flexDispatchOrder) && report.flexDispatchOrder.length > 0
    ? report.flexDispatchOrder
    : null;
  const dispatchOrder = Array.isArray(report.dispatchOrder) ? report.dispatchOrder : [];
  const costsByFuel = report.dispatchCosts || {};
  const meritOrderByFuel = Array.isArray(report.meritOrderByFuel) ? report.meritOrderByFuel : [];

  const simUsesVo = simulationDispatch?.dispatchMode === 'vo-report-tech';
  const simStack = Array.isArray(simulationDispatch?.flexDispatchOrder)
    ? simulationDispatch.flexDispatchOrder
    : null;
  const displayStack = simStack?.length ? simStack : flexStack;

  if (!report.dispatchAvailable && !flexStack) return null;

  return (
    <div className="mb-3 p-2 rounded border border-indigo-200 bg-indigo-50/40">
      <div className="font-semibold text-indigo-900 mb-1">Flex dispatch (Avg_cost_elec)</div>
      <p className="text-[10px] text-indigo-800/90 mb-2">
        Per-tech merit from report flex stack ($/MWh generation-weighted).
        Catalog overlays labels/parent fuel when a row matches; unlisted flex techs still dispatch.
        Zero model gen is last-resort. Chart generation is still rolled up by fuel.
        {report.costWeightMode ? (
          <span className="text-gray-600"> · weight={report.costWeightMode}</span>
        ) : null}
      </p>

      {!report.dispatchAvailable ? (
        <p className="text-[10px] text-amber-800">
          Avg_cost_elec missing in report — simulation uses legacy bio → coal → gas → oil order.
        </p>
      ) : (
        <>
          {displayStack?.length ? (
            <div className="mb-2 overflow-x-auto">
              <div className="text-[10px] font-semibold text-gray-700 mb-1">
                Merit order ({displayStack.length} techs)
              </div>
              <div className="max-h-56 overflow-auto">
                <table className="w-full text-[10px] border-collapse">
                  <thead>
                    <tr className="text-left text-gray-600 border-b border-indigo-100 sticky top-0 bg-indigo-50/95">
                      <th className="py-1 pr-2 font-medium">#</th>
                      <th className="py-1 pr-2 font-medium">Tech</th>
                      <th className="py-1 pr-2 font-medium">Fuel</th>
                      <th className="py-1 pr-2 font-medium">GW</th>
                      <th className="py-1 pr-2 font-medium">Model TWh</th>
                      <th className="py-1 pr-2 font-medium">$/MWh</th>
                      {simStack ? (
                        <>
                          <th className="py-1 pr-2 font-medium">Sim TWh</th>
                          <th className="py-1 font-medium">CF</th>
                        </>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {displayStack.map((row, idx) => (
                      <tr key={`${row.tech}-${row.fuel}-${idx}`} className="border-b border-indigo-50">
                        <td className="py-1 pr-2 font-mono text-gray-500">{idx + 1}</td>
                        <td className="py-1 pr-2">{row.tech || '—'}</td>
                        <td className="py-1 pr-2">{FLEX_FUEL_LABELS[row.fuel] || row.techByFuel || row.fuel || '—'}</td>
                        <td className="py-1 pr-2 font-mono">{row.capacityGw ?? '—'}</td>
                        <td className="py-1 pr-2 font-mono">{formatTwh(row.productionTwh)}</td>
                        <td className="py-1 pr-2 font-mono">{formatUsdPerMwh(row.costPerMwh, { lastResort: row.lastResort })}</td>
                        {simStack ? (
                          <>
                            <td className="py-1 pr-2 font-mono">{formatTwh(row.generationTWh)}</td>
                            <td className="py-1 font-mono">{formatCf(row.utilization)}</td>
                          </>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {Array.isArray(report.lastResortFlex) && report.lastResortFlex.length > 0 ? (
            <details className="mb-2">
              <summary className="text-[10px] cursor-pointer text-amber-800 font-medium">
                Last-resort flex ({report.lastResortFlex.length}) — in stack, lowest priority (zero model gen / no cost)
              </summary>
              <div className="mt-1 max-h-32 overflow-auto">
                <table className="w-full text-[10px] border-collapse">
                  <thead>
                    <tr className="text-left text-gray-600 border-b border-amber-100">
                      <th className="py-1 pr-2 font-medium">Tech</th>
                      <th className="py-1 pr-2 font-medium">Fuel</th>
                      <th className="py-1 pr-2 font-medium">GW</th>
                      <th className="py-1 pr-2 font-medium">Model TWh</th>
                      <th className="py-1 font-medium">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.lastResortFlex.map((row, idx) => (
                      <tr key={`lr-${row.tech}-${idx}`} className="border-b border-amber-50">
                        <td className="py-1 pr-2">{row.tech || '—'}</td>
                        <td className="py-1 pr-2">{FLEX_FUEL_LABELS[row.fuel] || row.fuel || '—'}</td>
                        <td className="py-1 pr-2 font-mono">{row.capacityGw ?? '—'}</td>
                        <td className="py-1 pr-2 font-mono">{formatTwh(row.productionTwh)}</td>
                        <td className="py-1 font-mono text-amber-900">{row.reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          ) : null}

          {Array.isArray(unmappedFlex) && unmappedFlex.length > 0 ? (
            <details className="mb-2">
              <summary className="text-[10px] cursor-pointer text-red-800 font-medium">
                Unmapped flex ({unmappedFlex.length}) — could not classify as bio/coal/gas/oil
              </summary>
              <div className="mt-1 max-h-32 overflow-auto">
                <table className="w-full text-[10px] border-collapse">
                  <thead>
                    <tr className="text-left text-gray-600 border-b border-red-100">
                      <th className="py-1 pr-2 font-medium">Tech</th>
                      <th className="py-1 pr-2 font-medium">tech_by_fuel</th>
                      <th className="py-1 pr-2 font-medium">GW</th>
                      <th className="py-1 font-medium">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmappedFlex.map((row, idx) => (
                      <tr key={`um-${row.tech}-${idx}`} className="border-b border-red-50">
                        <td className="py-1 pr-2">{row.tech || '—'}</td>
                        <td className="py-1 pr-2 font-mono">{row.techByFuel || '—'}</td>
                        <td className="py-1 pr-2 font-mono">{row.capacityGw ?? '—'}</td>
                        <td className="py-1 font-mono text-red-900">{row.reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          ) : null}

          {Object.keys(costsByFuel).length > 0 ? (
            <details className="mb-2">
              <summary className="text-[10px] cursor-pointer text-indigo-800 font-medium">
                Fuel-level averages (display only — generation-weighted, not used for dispatch)
              </summary>
              <table className="w-full text-[10px] border-collapse mt-1">
                <thead>
                  <tr className="text-left text-gray-600 border-b border-indigo-100">
                    <th className="py-1 pr-2 font-medium">Fuel</th>
                    <th className="py-1 pr-2 font-medium">Avg $/MWh</th>
                    <th className="py-1 font-medium">Old rank</th>
                  </tr>
                </thead>
                <tbody>
                  {['bio', 'coal', 'gas', 'oil']
                    .filter((fuel) => costsByFuel[fuel] != null)
                    .sort((a, b) => (costsByFuel[a] ?? Infinity) - (costsByFuel[b] ?? Infinity))
                    .map((fuel) => (
                      <tr key={fuel} className="border-b border-indigo-50">
                        <td className="py-1 pr-2">{FLEX_FUEL_LABELS[fuel] || fuel}</td>
                        <td className="py-1 pr-2 font-mono">{formatUsdPerMwh(costsByFuel[fuel])}</td>
                        <td className="py-1 font-mono text-gray-500">
                          {meritOrderByFuel.indexOf(fuel) >= 0 ? meritOrderByFuel.indexOf(fuel) + 1 : '—'}
                          {totals?.[FLEX_FUEL_TOTALS_KEYS[fuel]] != null
                            ? ` · ${totals[FLEX_FUEL_TOTALS_KEYS[fuel]]} GW`
                            : ''}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </details>
          ) : null}

          {dispatchOrder.length > 0 ? (
            <details className="mt-1">
              <summary className="text-[10px] cursor-pointer text-indigo-800 font-medium">
                All techs with cost ({dispatchOrder.length} rows, incl. non-flex)
              </summary>
              <div className="mt-2 max-h-40 overflow-auto">
                <table className="w-full text-[10px] border-collapse">
                  <thead>
                    <tr className="text-left text-gray-600 border-b border-indigo-100 sticky top-0 bg-indigo-50/95">
                      <th className="py-1 pr-2 font-medium">Tech</th>
                      <th className="py-1 pr-2 font-medium">Fuel</th>
                      <th className="py-1 pr-2 font-medium">GW</th>
                      <th className="py-1 pr-2 font-medium">Model TWh</th>
                      <th className="py-1 font-medium">$/MWh</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dispatchOrder.map((row, idx) => (
                      <tr key={`${row.tech}-${row.techByFuel}-${idx}`} className="border-b border-indigo-50">
                        <td className="py-1 pr-2">{row.tech || '—'}</td>
                        <td className="py-1 pr-2">{row.techByFuel || '—'}</td>
                        <td className="py-1 pr-2 font-mono">{row.capacityGw ?? '—'}</td>
                        <td className="py-1 pr-2 font-mono">{formatTwh(row.productionTwh)}</td>
                        <td className="py-1 font-mono">{formatUsdPerMwh(row.costPerMwh)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          ) : null}

          {simUsesVo ? (
            <div className="mt-2 text-[10px] text-emerald-800">
              Simulation uses per-tech merit order ✓
              {simStack?.length ? ` (${simStack.length} units)` : ''}
            </div>
          ) : report.dispatchAvailable ? (
            <div className="mt-2 text-[10px] text-amber-800">
              Report costs available; simulation not yet on VO dispatch (waiting for profiles?).
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

const VedaEngineTracePanel = ({
  lookup,
  result,
  trace,
  vreProfileTraces,
  simulationDispatch,
  unmappedFlex = [],
  loading,
  error,
  onDismiss,
}) => {
  const steps = trace?.steps || [];
  const meta = result?.meta;
  const [collapsed, setCollapsed] = useState(false);

  const summaryLine = useMemo(() => {
    if (!meta) return lookup ? `${lookup.region} · ${lookup.scenario}` : '';
    return `${meta.region} · ${meta.scenario} · ${meta.year} · ${meta.studyName}`;
  }, [lookup, meta]);

  const statusHint = useMemo(() => {
    if (loading) return 'Running…';
    if (error) return 'Error';
    if (trace?.steps?.length) return `${trace.steps.length} steps`;
    return null;
  }, [loading, error, trace?.steps?.length]);

  const copyTrace = () => {
    const blob = JSON.stringify({ lookup, result, trace, vreProfileTraces }, null, 2);
    navigator.clipboard?.writeText(blob).catch(() => {});
  };

  const hasVreTraces = vreProfileTraces && Object.keys(vreProfileTraces).length > 0;

  if (!lookup && !loading && !error && !trace) return null;

  return (
    <div
      className={`fixed bottom-4 left-4 z-[2000] w-[min(420px,calc(100vw-2rem))] flex flex-col rounded-lg border border-indigo-200 bg-white shadow-xl ${
        collapsed ? '' : 'max-h-[min(70vh,640px)]'
      }`}
    >
      <div
        className={`flex items-center justify-between gap-2 px-3 py-2 bg-indigo-50 rounded-t-lg ${
          collapsed ? 'rounded-b-lg' : 'border-b border-indigo-100'
        }`}
      >
        <button
          type="button"
          className="min-w-0 flex-1 flex items-start gap-2 text-left rounded hover:bg-indigo-100/80 -mx-1 px-1 py-0.5"
          onClick={() => setCollapsed((v) => !v)}
          title={collapsed ? 'Expand trace panel' : 'Collapse trace panel'}
        >
          {collapsed ? (
            <ChevronUp className="h-4 w-4 shrink-0 mt-0.5 text-indigo-700" />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0 mt-0.5 text-indigo-700" />
          )}
          <div className="min-w-0">
            <div className="text-xs font-semibold text-indigo-900">VedaOnline engine trace</div>
            {summaryLine ? (
              <div className="text-[10px] text-indigo-700 truncate" title={summaryLine}>
                {summaryLine}
                {statusHint ? (
                  <span className="text-indigo-500"> · {statusHint}</span>
                ) : null}
              </div>
            ) : statusHint ? (
              <div className="text-[10px] text-indigo-500">{statusHint}</div>
            ) : null}
          </div>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            className="p-1 rounded hover:bg-indigo-100 text-indigo-700"
            title="Copy full trace JSON"
            onClick={copyTrace}
          >
            <Copy className="h-4 w-4" />
          </button>
          {onDismiss ? (
            <button
              type="button"
              className="p-1 rounded hover:bg-indigo-100 text-indigo-700"
              title="Hide panel"
              onClick={onDismiss}
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>

      {!collapsed ? (
      <div className="flex-1 overflow-y-auto p-3 text-xs min-h-0">
        {loading ? (
          <div className="flex items-center gap-2 text-gray-600">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
            Running capacity engine…
          </div>
        ) : null}

        {error ? (
          <div className="mb-3 p-2 rounded border border-red-200 bg-red-50 text-red-800 text-[11px]">
            {error}
          </div>
        ) : null}

        {meta?.resultSchemaName ? (
          <div className="mb-3 p-2 rounded border border-gray-200 bg-gray-50">
            <div className="font-semibold text-gray-700">Result schema</div>
            <div className="font-mono text-[10px] break-all mt-1">{meta.resultSchemaName}</div>
          </div>
        ) : null}

        {meta?.degraded ? (
          <div className="mb-3 p-2 rounded border border-amber-200 bg-amber-50">
            <div className="font-semibold text-amber-900">Partial report data</div>
            <p className="text-[10px] text-amber-800 mt-1">
              {(meta.degradedReasons || []).join(', ') || 'Some optional varbls missing'}
            </p>
          </div>
        ) : null}

        {result?.report?.varblStatus ? (
          <div className="mb-3 p-2 rounded border border-gray-200 bg-gray-50">
            <div className="font-semibold text-gray-700 mb-1">Report varbls</div>
            <pre className="text-[10px] whitespace-pre-wrap break-words">
              {JSON.stringify(result.report.varblStatus, null, 2)}
            </pre>
          </div>
        ) : null}

        {result?.report?.demandTotalTwh != null ? (
          <div className="mb-3 p-2 rounded border border-gray-200 bg-gray-50">
            <div className="font-semibold text-gray-700 mb-1">Demand (report varbl)</div>
            <div className="text-[11px] font-mono">{result.report.demandTotalTwh} TWh</div>
            {result.report.productionTotalTwh != null ? (
              <div className="text-[10px] text-gray-500 mt-1">
                Elec Production (ref): {result.report.productionTotalTwh} TWh
              </div>
            ) : null}
          </div>
        ) : null}

        <DispatchMeritSection
          report={result?.report}
          totals={result?.totals}
          simulationDispatch={simulationDispatch}
          unmappedFlex={unmappedFlex}
        />

        {result?.totals ? (
          <div className="mb-3 p-2 rounded border border-gray-200 bg-gray-50">
            <div className="font-semibold text-gray-700 mb-1">Totals (GW)</div>
            <pre className="text-[10px] whitespace-pre-wrap break-words">
              {JSON.stringify(result.totals, null, 2)}
            </pre>
          </div>
        ) : null}

        {result?.gaps?.vreCoverage ? (
          <div className="mb-3 p-2 rounded border border-amber-200 bg-amber-50">
            <div className="font-semibold text-amber-900 mb-1">VRE cluster coverage (report vs VO)</div>
            <pre className="text-[10px] whitespace-pre-wrap break-words text-amber-900">
              {JSON.stringify(result.gaps.vreCoverage, null, 2)}
            </pre>
          </div>
        ) : null}

        {result?.vre ? (
          <div className="mb-3 p-2 rounded border border-gray-200 bg-gray-50">
            <div className="font-semibold text-gray-700 mb-1">VO clusters</div>
            <pre className="text-[10px] whitespace-pre-wrap break-words">
              {JSON.stringify({
                solar: {
                  totalGw: result.vre.solar?.totalGw ?? 0,
                  clusters: result.vre.solar?.clusters?.length ?? 0,
                },
                windOn: {
                  totalGw: result.vre.windOn?.totalGw ?? 0,
                  clusters: result.vre.windOn?.clusters?.length ?? 0,
                },
                windOff: {
                  totalGw: result.vre.windOff?.totalGw ?? 0,
                  clusters: result.vre.windOff?.clusters?.length ?? 0,
                },
              }, null, 2)}
            </pre>
          </div>
        ) : null}

        {hasVreTraces ? (
          <div className="mb-3">
            <div className="font-semibold text-gray-800 mb-1">VRE profile combine (verification)</div>
            <p className="text-[10px] text-gray-600 mb-2">
              National MW[h] = Σ (cluster_GW × 1000 × CF[h]). Per-cluster implied CF should match DB avg_cf.
            </p>
            {Object.entries(vreProfileTraces).map(([key, entry]) => (
              <div key={key} className="mb-2 p-2 rounded border border-indigo-100 bg-indigo-50/50">
                <div className="font-medium text-indigo-900 text-[11px]">
                  {VRE_TECH_LABELS[key] || key}
                </div>
                {entry.status_messages?.length ? (
                  <ul className="text-[10px] text-gray-700 mt-1 list-disc pl-4">
                    {entry.status_messages.map((msg) => (
                      <li key={msg}>{msg}</li>
                    ))}
                  </ul>
                ) : null}
                {entry.trace?.combined_profile_stats ? (
                  <pre className="text-[10px] mt-2 whitespace-pre-wrap break-words bg-white/80 p-2 rounded border">
                    {JSON.stringify(entry.trace.combined_profile_stats, null, 2)}
                  </pre>
                ) : null}
                {entry.trace?.profile_coverage ? (
                  <pre className="text-[10px] mt-2 whitespace-pre-wrap break-words bg-white/80 p-2 rounded border">
                    {JSON.stringify(entry.trace.profile_coverage, null, 2)}
                  </pre>
                ) : entry.coverage ? (
                  <pre className="text-[10px] mt-2 whitespace-pre-wrap break-words bg-white/80 p-2 rounded border">
                    {JSON.stringify(entry.coverage, null, 2)}
                  </pre>
                ) : null}
                {entry.trace?.cluster_contributions?.length ? (
                  <details className="mt-2">
                    <summary className="text-[10px] cursor-pointer text-indigo-800">
                      Per-cluster contributions ({entry.trace.cluster_contributions.length})
                    </summary>
                    <pre className="text-[10px] mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-words bg-white/80 p-2 rounded border">
                      {JSON.stringify(entry.trace.cluster_contributions, null, 2)}
                    </pre>
                  </details>
                ) : null}
                {entry.trace?.profiles_missing?.length ? (
                  <pre className="text-[10px] mt-2 text-amber-800 whitespace-pre-wrap break-words">
                    Missing: {JSON.stringify(entry.trace.profiles_missing, null, 2)}
                  </pre>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {steps.length > 0 ? (
          <div>
            <div className="font-semibold text-gray-800 mb-2">
              Steps ({steps.length})
              {trace?.durationMs != null ? (
                <span className="font-normal text-gray-500 ml-1">· {trace.durationMs}ms total</span>
              ) : null}
            </div>
            {steps.map((step) => (
              <StepBlock key={step.id} step={step} />
            ))}
          </div>
        ) : null}
      </div>
      ) : null}
    </div>
  );
};

export default VedaEngineTracePanel;
